import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FaHandshake, FaSyncAlt } from 'react-icons/fa';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';
import OrgBuyersPanel from './OrgBuyersPanel';

// GH-676 — "שותפים": review + approve the supplier applications submitted from
// partners.grouphug.online (POST /partners/public/apply).

type Category = { slug: string; he: string; en: string };

type Product = {
	name: string;
	category?: string; // GH-685 — slug; labels come from GET /partners/public/categories
	description?: string;
	terms?: string;
	price?: number;
	min_order?: string;
	sla?: string;
	images: string[];
};

const contactName = (p: { contact_first_name?: string; contact_last_name?: string }) =>
	`${p.contact_first_name || ''} ${p.contact_last_name || ''}`.trim();

type PartnerRow = {
	_id: string;
	brand_name: string;
	brand_name_en?: string;
	trade_name?: string;
	dealer_type?: string;
	business_id?: string;
	email: string;
	phone?: string;
	contact_first_name?: string; // GH-824 — day-to-day contact person
	contact_last_name?: string;
	contact_phone?: string;
	address?: string;
	order_hours?: { days?: string[]; from?: string; to?: string }[];
	service_languages?: string[];
	description?: string;
	kosher?: string;
	license_expiry?: string;
	insurance_expiry?: string;
	bank_account_owner?: string;
	bank_name?: string;
	bank_branch?: string;
	bank_account?: string;
	doc_bank_approval?: string;
	doc_license_insurance?: string;
	doc_signed_agreement?: string;
	products: Product[];
	recommendations: { client?: string; text?: string; file?: string }[];
	commission_rate: number;
	status: 'submitted' | 'in_review' | 'approved' | 'rejected';
	status_note?: string;
	createdAt: string;
};

const STATUS_LABELS: Record<PartnerRow['status'], { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }> = {
	submitted: { label: 'ממתין לבדיקה', color: 'warning' },
	in_review: { label: 'בבדיקה', color: 'info' },
	approved: { label: 'מאושר · באוויר', color: 'success' },
	rejected: { label: 'נדחה', color: 'error' },
};

const docLink = (url?: string, label?: string) =>
	url ? (
		<a href={url} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', fontWeight: 600 }}>
			{label} ↗
		</a>
	) : (
		<span style={{ color: '#b3261e' }}>{label} — חסר</span>
	);

export default function PartnersDashboard() {
	const [rows, setRows] = useState<PartnerRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<string>('');
	const [selected, setSelected] = useState<PartnerRow | null>(null);
	const [note, setNote] = useState('');
	const [saving, setSaving] = useState(false);
	const [categories, setCategories] = useState<Category[]>([]);
	useEffect(() => {
		httpClient(`${constants.API_URL}partners/public/categories`, { method: 'GET' })
			.then((res) => setCategories((res.json as Category[]) || []))
			.catch(() => setCategories([]));
	}, []);
	const categoryLabel = (slug?: string) => categories.find((c) => c.slug === slug)?.he || '';
	// Two audiences on one page: suppliers (applications) and organisations / HR (buyers).
	const [tab, setTab] = useState<'suppliers' | 'orgs'>('suppliers');

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await httpClient(
				`${constants.API_URL}partners/admin/applications${statusFilter ? `?status=${statusFilter}` : ''}`,
				{ method: 'GET' },
			);
			setRows((res.json as PartnerRow[]) || []);
		} finally {
			setLoading(false);
		}
	}, [statusFilter]);

	useEffect(() => {
		load();
	}, [load]);

	const setStatus = async (id: string, status: PartnerRow['status']) => {
		setSaving(true);
		try {
			await httpClient(`${constants.API_URL}partners/admin/${id}/status`, {
				method: 'POST',
				body: JSON.stringify({ status, note }),
			});
			setSelected(null);
			setNote('');
			await load();
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box p={3}>
			<Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
				<Box display="flex" alignItems="center" gap={1}>
					<FaHandshake size={22} />
					<Typography variant="h5" fontWeight={700}>
						שותפים
					</Typography>
					<Box display="flex" gap={1} sx={{ mr: 2 }}>
						<Button size="small" variant={tab === 'suppliers' ? 'contained' : 'outlined'} onClick={() => setTab('suppliers')}>
							ספקים — בקשות הצטרפות
						</Button>
						<Button size="small" variant={tab === 'orgs' ? 'contained' : 'outlined'} onClick={() => setTab('orgs')}>
							ארגונים / HR
						</Button>
					</Box>
				</Box>
				<Box display="flex" alignItems="center" gap={2} sx={{ visibility: tab === 'orgs' ? 'hidden' : 'visible' }}>
					<Select
						size="small"
						displayEmpty
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
					>
						<MenuItem value="">כל הסטטוסים</MenuItem>
						<MenuItem value="submitted">ממתין לבדיקה</MenuItem>
						<MenuItem value="in_review">בבדיקה</MenuItem>
						<MenuItem value="approved">מאושר</MenuItem>
						<MenuItem value="rejected">נדחה</MenuItem>
					</Select>
					<Button startIcon={<FaSyncAlt />} onClick={load}>
						רענון
					</Button>
				</Box>
			</Box>

			{tab === 'orgs' ? (
				<OrgBuyersPanel />
			) : loading ? (
				<Box textAlign="center" py={6}>
					<CircularProgress />
				</Box>
			) : rows.length === 0 ? (
				<Typography color="text.secondary">אין בקשות{statusFilter ? ' בסטטוס הזה' : ' עדיין'}.</Typography>
			) : (
				<Table>
					<TableHead>
						<TableRow>
							<TableCell align="right">תאריך</TableCell>
							<TableCell align="right">מותג</TableCell>
							<TableCell align="right">סוג עוסק</TableCell>
							<TableCell align="right">מייל</TableCell>
							<TableCell align="right">איש קשר</TableCell>
							<TableCell align="right">טלפון העסק</TableCell>
							<TableCell align="right">מוצרים</TableCell>
							<TableCell align="right">מסמכים</TableCell>
							<TableCell align="right">סטטוס</TableCell>
							<TableCell align="right"></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((r) => {
							const st = STATUS_LABELS[r.status];
							const docsOk = [r.doc_bank_approval, r.doc_license_insurance, r.doc_signed_agreement].filter(Boolean).length;
							return (
								<TableRow key={r._id} hover>
									<TableCell align="right">{new Date(r.createdAt).toLocaleDateString('he-IL')}</TableCell>
									<TableCell align="right">
										<b>{r.brand_name}</b>
										{r.brand_name_en ? <span style={{ color: '#64748b' }}> · {r.brand_name_en}</span> : null}
									</TableCell>
									<TableCell align="right">{r.dealer_type || '—'}</TableCell>
									<TableCell align="right">{r.email}</TableCell>
									<TableCell align="right">
										{contactName(r) || '—'}
										{r.contact_phone ? <div style={{ color: '#64748b', direction: 'ltr', textAlign: 'right' }}>{r.contact_phone}</div> : null}
									</TableCell>
									<TableCell align="right">{r.phone || '—'}</TableCell>
									<TableCell align="right">{r.products?.length || 0}</TableCell>
									<TableCell align="right">{docsOk}/3</TableCell>
									<TableCell align="right">
										<Chip size="small" color={st.color} label={st.label} />
									</TableCell>
									<TableCell align="right">
										<Button size="small" onClick={() => { setSelected(r); setNote(r.status_note || ''); }}>
											פרטים
										</Button>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			)}

			{/* Application detail + decision dialog */}
			<Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth dir="rtl">
				{selected && (
					<>
						<DialogTitle>
							{selected.brand_name}{' '}
							<Chip size="small" color={STATUS_LABELS[selected.status].color} label={STATUS_LABELS[selected.status].label} sx={{ mr: 1 }} />
						</DialogTitle>
						<DialogContent dividers>
							<Typography variant="subtitle2" fontWeight={700} gutterBottom>פרטי עסק</Typography>
							<Typography variant="body2" component="div" sx={{ lineHeight: 2 }}>
								{selected.trade_name || '—'} · {selected.dealer_type || '—'} · ח.פ {selected.business_id || '—'}
								<br />
								איש קשר לקשר שוטף: {contactName(selected) || '—'}
								{selected.contact_phone ? <> · <span dir="ltr">{selected.contact_phone}</span></> : null}
								<br />
								{selected.email} · טלפון העסק {selected.phone || '—'} · {selected.address || '—'}
								<br />
								שעות הזמנות: {selected.order_hours?.length ? selected.order_hours.map((w) => `${(w.days || []).join(', ')} ${w.from || ''}–${w.to || ''}`).join(' | ') : '—'} · שפות: {selected.service_languages?.join(', ') || '—'}
								<br />
								כשרות: {selected.kosher || '—'}
								<br />
								תוקף רישיון: {selected.license_expiry ? new Date(selected.license_expiry).toLocaleDateString('he-IL') : '—'} · תוקף ביטוח:{' '}
								{selected.insurance_expiry ? new Date(selected.insurance_expiry).toLocaleDateString('he-IL') : '—'}
							</Typography>
							{selected.description && (
								<Typography variant="body2" sx={{ mt: 1, color: '#475569' }}>{selected.description}</Typography>
							)}

							<Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }} gutterBottom>מסמכים</Typography>
							<Typography variant="body2" component="div" sx={{ lineHeight: 2 }}>
								{docLink(selected.doc_bank_approval, 'אישור ניהול חשבון')}
								{' · '}
								{docLink(selected.doc_license_insurance, 'רישיון עסק + ביטוח')}
								{' · '}
								{docLink(selected.doc_signed_agreement, 'הסכם חתום')}
							</Typography>

							<Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }} gutterBottom>
								בנק (לתשלומים)
							</Typography>
							<Typography variant="body2">
								{selected.bank_account_owner || '—'} · {selected.bank_name || '—'} · סניף {selected.bank_branch || '—'} · חשבון {selected.bank_account || '—'}
							</Typography>

							<Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }} gutterBottom>
								קטלוג ({selected.products?.length || 0})
							</Typography>
							{(selected.products || []).map((p, i) => (
								<Box key={i} sx={{ mb: 1.5, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
									<Typography variant="body2" fontWeight={700}>
										{p.name}
										{p.category ? (
											<Chip size="small" variant="outlined" label={categoryLabel(p.category) || p.category} sx={{ mx: 1 }} />
										) : (
											<Chip size="small" color="warning" label="ללא קטגוריה" sx={{ mx: 1 }} />
										)}
										{p.price != null ? `· ₪${p.price}` : ''} {p.min_order ? `· מינ׳ ${p.min_order}` : ''} {p.sla ? `· התראה: ${p.sla}` : ''}
									</Typography>
									{p.description && <Typography variant="body2" color="text.secondary">{p.description}</Typography>}
									{p.images?.length > 0 && (
										<Box display="flex" gap={1} mt={1} flexWrap="wrap">
											{p.images.map((img, j) => (
												<a key={j} href={img} target="_blank" rel="noreferrer">
													<img src={img} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }} />
												</a>
											))}
										</Box>
									)}
								</Box>
							))}

							{(selected.recommendations?.length || 0) > 0 && (
								<>
									<Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2 }} gutterBottom>המלצות</Typography>
									{selected.recommendations.map((rec, i) => (
										<Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
											{rec.client ? <b>{rec.client}: </b> : null}
											{rec.text || ''} {rec.file ? docLink(rec.file, 'קובץ') : null}
										</Typography>
									))}
								</>
							)}

							<TextField
								label="הערה פנימית (תישמר על הסטטוס)"
								fullWidth
								size="small"
								sx={{ mt: 3 }}
								value={note}
								onChange={(e) => setNote(e.target.value)}
							/>
						</DialogContent>
						<DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
							<Button color="error" disabled={saving} onClick={() => setStatus(selected._id, 'rejected')}>
								דחייה
							</Button>
							<Box display="flex" gap={1}>
								<Button disabled={saving} onClick={() => setStatus(selected._id, 'in_review')}>
									סמן בבדיקה
								</Button>
								<Button variant="contained" color="success" disabled={saving} onClick={() => setStatus(selected._id, 'approved')}>
									אישור — לעלות לאוויר ✓
								</Button>
							</Box>
						</DialogActions>
					</>
				)}
			</Dialog>
		</Box>
	);
}
